import { ClerkProvider, useClerk, useSession, useUser } from '@clerk/react';
import { ClerkRole } from '@shared/constants';
// @ts-expect-error js-cookie has no declaration file
import Cookies from 'js-cookie';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { ClerkRouterNavigation } from './lib/routing';
import { CUBE_TOKEN_COOKIE, CUBE_TOKEN_EXPIRY_COOKIE, CUBE_TOKEN_ORG_COOKIE } from './providers/CubeTokenProvider';

const isE2E = process.env.AGRIFUL_ENV === 'e2e' || process.env.E2E_MODE === 'true';

export interface CurrentUser {
  [key: string]: unknown; // index signature for Record<string, unknown> compat
  id: string;
  emailAddress: string | string[];
  firstName: string;
  lastName: string;
  roles: string[];
  userId: number;
  knockId: string;
  organizationId?: number;
  organizationClerkId?: string;
  guest?: boolean;
  membershipId?: number;
  guestMembershipId?: number;
}

interface AuthContextValue {
  loading: boolean;
  isAuthenticated: boolean;
  currentUser: CurrentUser | null;
  userMetadata: Record<string, any> | null;
  getToken: () => Promise<string | null>;
  logIn: (options?: any) => Promise<void>;
  logOut: (options?: any) => Promise<void>;
  signUp: (options?: any) => Promise<void>;
  reauthenticate: () => Promise<void>;
  getCurrentUser: () => Promise<CurrentUser | null>;
  hasRole: (rolesToCheck: string | string[]) => boolean;
  hasError: boolean;
  error?: Error;
  client: any;
  type: string;
  // Required by RedwoodApolloProvider types
  forgotPassword: (username: string) => Promise<any>;
  resetPassword: (options?: any) => Promise<any>;
  validateResetToken: (token: string | null) => Promise<any>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

const cookies = [CUBE_TOKEN_COOKIE, CUBE_TOKEN_EXPIRY_COOKIE, CUBE_TOKEN_ORG_COOKIE];

function AuthStateProvider({ children }: { children: React.ReactNode }) {
  const { session, isLoaded: isSessionLoaded } = useSession();
  const { user } = useUser();
  const clerk = useClerk();
  const e2eClerk = isE2E && typeof window !== 'undefined' ? (window as any).Clerk : null;

  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      if (e2eClerk) return e2eClerk.session?.getToken() ?? null;
      return (await session?.getToken()) ?? null;
    } catch {
      return null;
    }
  }, [e2eClerk, session]);

  // Uses RW's built-in __REDWOOD__AUTH_GET_CURRENT_USER query, handled by
  // createGraphQLHandler → getCurrentUser() in api/src/lib/auth.ts
  const fetchCurrentUser = useCallback(async (): Promise<CurrentUser | null> => {
    const token = await getToken();
    if (!token) return null;

    const g = globalThis as Record<string, any>;
    const url =
      (g['RWJS_API_GRAPHQL_URL'] as string | undefined) ??
      `${g['__REDWOOD__API_PROXY_PATH'] as string | undefined}/graphql`;

    const doRequest = (t: string) =>
      fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', 'auth-provider': 'clerk', authorization: `Bearer ${t}` },
        body: JSON.stringify({ query: 'query __REDWOOD__AUTH_GET_CURRENT_USER { redwood { currentUser } }' }),
      });

    let response = await doRequest(token);
    let retried = false;

    if (response.status === 401) {
      const fresh = e2eClerk
        ? await e2eClerk.session?.getToken({ skipCache: true })
        : await session?.getToken({ skipCache: true });
      if (fresh && fresh !== token) {
        response = await doRequest(fresh);
        retried = true;
      }
    }

    if (response.ok) {
      const { data } = await response.json();
      return data?.redwood?.currentUser ?? null;
    }
    if (response.status === 401 || response.status === 500) {
      console.warn(
        retried
          ? '[fetchCurrentUser] swallowed non-OK after retry'
          : '[fetchCurrentUser] swallowed non-OK (no retry — no fresh token available)',
        { status: response.status, statusText: response.statusText }
      );
      return null;
    }
    throw new Error(`Could not fetch current user: ${response.statusText} (${response.status})`);
  }, [getToken, session, e2eClerk]);

  const reauthenticate = useCallback(async () => {
    setLoading(true);
    try {
      const cu = await fetchCurrentUser();
      if (cu === null && session?.id) {
        console.warn(
          '[reauthenticate] fetchCurrentUser returned null while Clerk session is active; preserving previous currentUser'
        );
        setHasError(false);
        setError(undefined);
        return;
      }
      setCurrentUser(cu);
      setHasError(false);
      setError(undefined);
    } catch (e) {
      // Symmetric with the try-branch preserve: when Clerk says the session is
      // alive, this is the "trust Clerk" path — no state change at all (don't
      // null currentUser, don't surface an error). Only if Clerk has also
      // dropped the session do we treat this as a real auth failure.
      if (!session?.id) {
        setCurrentUser(null);
        setHasError(true);
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      setLoading(false);
    }
  }, [fetchCurrentUser, session?.id]);

  const reauthenticateRef = useRef(reauthenticate);
  reauthenticateRef.current = reauthenticate;

  // E2E: Clerk v6 useSession() depends on useSyncExternalStore which never
  // resolves with the mock. Bypass it — the mock provides tokens directly.
  const effectiveIsSessionLoaded = e2eClerk ? true : isSessionLoaded;
  const effectiveSessionId = e2eClerk ? 'sess_e2e' : session?.id;

  useEffect(() => {
    if (!effectiveIsSessionLoaded) return;
    if (effectiveSessionId) {
      reauthenticateRef.current();
    } else {
      setCurrentUser(null);
      setLoading(false);
    }
  }, [effectiveIsSessionLoaded, effectiveSessionId]);

  useEffect(() => {
    if (effectiveIsSessionLoaded && !session && !e2eClerk) {
      cookies.forEach((cookie) => Cookies.remove(cookie, { path: '' }));
    }
  }, [effectiveIsSessionLoaded, session, e2eClerk]);

  const isAuthenticated = !!currentUser;

  const hasRole = useCallback(
    (rolesToCheck: string | string[]): boolean => {
      if (!currentUser?.roles) return false;
      const roles = Array.isArray(rolesToCheck) ? rolesToCheck : [rolesToCheck];
      return currentUser.roles.some((r: string) => roles.includes(r));
    },
    [currentUser]
  );

  // Stabilize callback identities so the AuthContext value below can be memoized.
  // Without this, every render produces fresh function references, which forces
  // every useAuth() consumer (notably RedwoodApolloProvider, which then calls
  // `new ApolloClient(...)`) to re-render and refetch on every Clerk session emit.
  const logIn = useCallback<AuthContextValue['logIn']>(async (options) => clerk.openSignIn(options ?? {}), [clerk]);

  const logOut = useCallback<AuthContextValue['logOut']>(
    async (options) => {
      await clerk.signOut(options);
    },
    [clerk]
  );

  const signUp = useCallback<AuthContextValue['signUp']>(async (options) => clerk.openSignUp(options ?? {}), [clerk]);

  const forgotPassword = useCallback<AuthContextValue['forgotPassword']>(async () => {
    throw new Error('forgotPassword is not supported with Clerk');
  }, []);

  const resetPassword = useCallback<AuthContextValue['resetPassword']>(async () => {
    throw new Error('resetPassword is not supported with Clerk');
  }, []);

  const validateResetToken = useCallback<AuthContextValue['validateResetToken']>(async () => {
    throw new Error('validateResetToken is not supported with Clerk');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading: !effectiveIsSessionLoaded || loading,
      isAuthenticated,
      currentUser,
      userMetadata: user ?? null,
      getToken,
      logIn,
      logOut,
      signUp,
      reauthenticate,
      getCurrentUser: fetchCurrentUser,
      hasRole,
      hasError,
      error,
      client: e2eClerk ?? undefined,
      type: 'clerk',
      forgotPassword,
      resetPassword,
      validateResetToken,
    }),
    [
      effectiveIsSessionLoaded,
      loading,
      isAuthenticated,
      currentUser,
      user,
      getToken,
      logIn,
      logOut,
      signUp,
      reauthenticate,
      fetchCurrentUser,
      hasRole,
      hasError,
      error,
      e2eClerk,
      forgotPassword,
      resetPassword,
      validateResetToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface AuthProviderProps {
  children: React.ReactNode;
  // Supplied by App.tsx. Omitted only in tests, where Clerk itself is mocked.
  navigation?: ClerkRouterNavigation;
}

export const AuthProvider = ({ children, navigation }: AuthProviderProps) => {
  const publishableKey = process.env.CLERK_PUBLISHABLE_KEY ?? '';

  return (
    <ClerkProvider publishableKey={publishableKey} routerPush={navigation?.push} routerReplace={navigation?.replace}>
      <AuthStateProvider>{children}</AuthStateProvider>
    </ClerkProvider>
  );
};

export function useIsMember() {
  const { currentUser } = useAuth();
  return currentUser?.roles.includes(ClerkRole.Member) || currentUser?.roles.includes(ClerkRole.Admin);
}

export function useIsCustomer() {
  const { currentUser } = useAuth();
  return currentUser?.roles.includes(ClerkRole.Customer);
}

export function useIsOrgAdmin() {
  const { currentUser } = useAuth();
  return currentUser?.roles.includes(ClerkRole.Admin);
}

// TODO: Remove when Roles and Permissions are implemented!
// Hardcoded gate for Dawson's warehouse user — they must not see any accounting
// surface. Must be honored in Routes.tsx (route registration) AND at every
// render site that calls routes.<accounting>() or renders accounting UI.
export function useIsDawsonsWarehouseUser() {
  const { currentUser } = useAuth();
  return currentUser?.id === 'user_2qpu9ix5xpDXvltlxgmMBmrqs3h';
}
